import type {
  ItemCorrectionsResource,
  ItemStockStateResource,
  ItemTradingDayResource,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { TableCell } from '@repo/ui/components/table';

import { formatPaise } from '@/lib/money';

import { formatBusinessDate } from '../tickets/business-date';
import {
  describeStock,
  formatQuantity,
  formatSignedQuantity,
  readStock,
  STOCK_NOT_APPLICABLE,
  STOCK_NOT_COUNTED,
  writtenOffQuantity,
} from './item-activity';

/**
 * The cells the two item reports share: `Items` (the owner's, with cost and
 * margin) and `ItemSales` (the manager's, with neither).
 *
 * **Nothing in this file can draw a cost.** {@link ItemFlows} is picked off the
 * manager's `ItemTradingDayResource`, which carries no `costPaise` and no
 * `marginPaise` at any nesting, so a cost cell added here would not typecheck
 * against it. The owner's screen passes its own rows in — they carry every one
 * of these fields and more — and draws its margin column itself, beside these.
 *
 * **Every figure comes from the row it is drawn for**, and nothing here totals
 * anything. `stock.countedQuantity` is a level and not a flow, so a cell that
 * summed it across days would be wrong in a way nobody notices until a stock
 * count is disputed; `Items.tsx`'s head has the whole of the argument.
 */

/** The flows and stock of one row, range or day — and, deliberately, no cost. */
export type ItemFlows = Pick<
  ItemTradingDayResource,
  | 'soldQuantity'
  | 'soldValuePaise'
  | 'refundedQuantity'
  | 'refundedValuePaise'
  | 'voidedQuantity'
  | 'voidedValuePaise'
  | 'corrections'
  | 'stock'
>;

/**
 * The seven measure cells after the item, drawn identically for a range row and
 * a day row.
 *
 * One component rather than one per row kind, because the two rows must not be
 * able to drift apart about what a column means: a counted cell that behaved
 * differently on a day row than on the row above it is precisely the bug these
 * screens are written against.
 */
export function FlowMeasures({ figures }: { figures: ItemFlows }) {
  return (
    <>
      <Pair quantity={figures.soldQuantity} valuePaise={figures.soldValuePaise} />
      <Pair quantity={figures.refundedQuantity} valuePaise={figures.refundedValuePaise} />
      <Pair quantity={figures.voidedQuantity} valuePaise={figures.voidedValuePaise} />
      <Count>{figures.corrections.deliveredQuantity}</Count>
      <WrittenOff corrections={figures.corrections} />
      <CountedCell stock={figures.stock} />
      <VarianceCell stock={figures.stock} />
    </>
  );
}

/** The item's code and name, and a mark if it has been retired since. */
export function ItemNameCell({
  code,
  name,
  retired,
}: {
  code: string;
  name: string;
  retired: boolean;
}) {
  return (
    <TableCell>
      <div className="flex items-center gap-2">
        <span className="font-mono text-muted-foreground numeric">{code}</span>
        <span className="text-foreground">{name}</span>
        {retired && (
          // `outline` and not `destructive`: a retired item that traded in the
          // period is still part of the record, and marking it as a problem
          // would suggest the figures beside it are one.
          <Badge variant="outline">Retired</Badge>
        )}
      </div>
    </TableCell>
  );
}

/**
 * A day inside an item's breakdown: the date, indented, and whether it is
 * settled yet.
 *
 * `state` is `OPEN` or `CLOSED` and is never null on these reads — every leg of
 * the breakdown is driven from `business_days` under the same predicate the
 * period envelope uses, so a date appearing here is a date the envelope named
 * and its state is always resolved. An open day's figures are provisional and
 * the row says so, which is the whole reason the field is published; it is a
 * statement about reconciliation and not about the figures.
 */
export function DayLabel({ businessDate, state }: { businessDate: string; state: string }) {
  return (
    <TableCell>
      <span className="flex items-center gap-2 ps-6 text-sm text-muted-foreground">
        {formatBusinessDate(businessDate)}
        {state === 'OPEN' && <Badge variant="outline">Not closed yet</Badge>}
      </span>
    </TableCell>
  );
}

/**
 * A quantity with the money it moved beneath it.
 *
 * The pairing is presentational and never arithmetic: both figures are printed
 * and neither is folded into the other. It exists because twelve units handed
 * back and six hundred rupees handed back are two halves of one fact, and a
 * screen that split them across two columns would put nine columns of figures
 * between them.
 *
 * The visible pair is `aria-hidden` and a sentence takes its place, because
 * "12 ₹600.00" run together is ambiguous enough to be worth saying properly.
 */
function Pair({ quantity, valuePaise }: { quantity: number; valuePaise: number }) {
  return (
    <TableCell className="text-right">
      <span className="sr-only">
        {formatQuantity(quantity)} for {formatPaise(valuePaise)}
      </span>
      <span aria-hidden className="block font-mono text-foreground numeric">
        {formatQuantity(quantity)}
      </span>
      <span aria-hidden className="block font-mono text-xs text-muted-foreground numeric">
        {formatPaise(valuePaise)}
      </span>
    </TableCell>
  );
}

/** A count, right-aligned and set in figures that do not jitter down a column. */
function Count({ children }: { children: number }) {
  return <TableCell className="text-right font-mono numeric">{formatQuantity(children)}</TableCell>;
}

/**
 * What came off the shelf other than by selling it, with the four reasons under
 * the figure.
 *
 * **There is no way to render the total without them.** Six units gone is one
 * fact; six broken, six spoiled and six stolen are three, and only the last of
 * those is a conversation with a person. The delivery is not in this total and
 * has its own column, per the five measures the reads keep apart.
 */
function WrittenOff({ corrections }: { corrections: ItemCorrectionsResource }) {
  const total = writtenOffQuantity(corrections);

  const reasons = [
    { label: 'broken', quantity: corrections.breakageQuantity },
    { label: 'spoiled', quantity: corrections.spoilageQuantity },
    { label: 'lost', quantity: corrections.lossQuantity },
    { label: 'found', quantity: corrections.foundQuantity },
  ].filter((reason) => reason.quantity !== 0);

  return (
    <TableCell className="text-right">
      <span className="sr-only">
        {total === 0
          ? 'Nothing written off.'
          : `${formatSignedQuantity(total)} off the shelf: ${reasons
              .map((reason) => `${formatQuantity(Math.abs(reason.quantity))} ${reason.label}`)
              .join(', ')}.`}
      </span>
      <span aria-hidden className="block font-mono text-foreground numeric">
        {formatSignedQuantity(total)}
      </span>
      {reasons.length > 0 && (
        // Wrapping, and capped at a measure. The registry's cells are
        // `whitespace-nowrap`, so four reasons on one line made this the widest
        // column in the table and pushed Counted and Variance — the two columns
        // these screens exist for — off the right edge at 1280. Two short lines
        // here cost nothing and keep every column on screen at a desk width.
        <span
          aria-hidden
          className="ms-auto block max-w-32 text-xs leading-snug whitespace-normal text-muted-foreground"
        >
          {reasons
            .map((reason) => `${String(Math.abs(reason.quantity))} ${reason.label}`)
            .join(' · ')}
        </span>
      )}
    </TableCell>
  );
}

/**
 * The count, and the two readings that are **not** a count of nought.
 *
 * A shelf nobody counted and a shelf counted and found empty are different
 * facts. `NOT_COUNTED` therefore says so in words — not `0`, and not a dash,
 * which reads as one — and `NOT_APPLICABLE` says something different again,
 * because an outlet with no shelf was never going to have a figure here.
 *
 * The figure itself is the **latest** count inside the period, which is a level.
 * It is read off this row's own `stock` and is never derived from the day rows
 * beneath it. `Items.test.tsx` holds that against a fixture no derivation can
 * satisfy.
 */
function CountedCell({ stock }: { stock: ItemStockStateResource }) {
  const reading = readStock(stock);

  return (
    <TableCell className="text-right">
      <span className="sr-only">{describeStock(stock)}</span>
      {reading.kind === 'COUNTED' ? (
        <span aria-hidden className="font-mono text-foreground numeric">
          {formatQuantity(reading.quantity)}
        </span>
      ) : (
        <span aria-hidden className="text-xs text-muted-foreground">
          {reading.kind === 'NOT_COUNTED' ? STOCK_NOT_COUNTED : STOCK_NOT_APPLICABLE}
        </span>
      )}
    </TableCell>
  );
}

/**
 * `counted − computed`, totalled over every count in the period.
 *
 * A flow, unlike the count beside it, so the days do add up to the range figure
 * — and the API computes both ends, so neither is worked out here. **Zero is a
 * real answer** and means a count that agreed exactly, which is why it prints
 * plainly rather than as a dash: this is the second half of FR-904's
 * distinction, and it is the column where a nil and a nought are most easily
 * confused. Where there was no count there is no variance, and that says the
 * same words the counted cell does.
 */
function VarianceCell({ stock }: { stock: ItemStockStateResource }) {
  const reading = readStock(stock);

  if (reading.kind !== 'COUNTED' || stock.varianceQuantity === null) {
    return (
      <TableCell className="text-right">
        <span className="sr-only">No count, so no variance. That is not a variance of nought.</span>
        <span aria-hidden className="text-xs text-muted-foreground">
          {reading.kind === 'NOT_APPLICABLE' ? STOCK_NOT_APPLICABLE : STOCK_NOT_COUNTED}
        </span>
      </TableCell>
    );
  }

  return (
    <TableCell className="text-right font-mono numeric">
      {formatSignedQuantity(stock.varianceQuantity)}
    </TableCell>
  );
}
